import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useBaseContext, createBaseState } from './shared/useBaseContext';
import { useAuth } from '../hooks/useAuth';

const ChatContext = createContext();

// Chat-specific actions
const chatActions = {
  SET_MESSAGES: 'SET_MESSAGES',
  ADD_MESSAGE: 'ADD_MESSAGE',
  SET_ONLINE_USERS: 'SET_ONLINE_USERS',
  START_TYPING: 'START_TYPING',
  STOP_TYPING: 'STOP_TYPING',
  SET_IS_EXPANDED: 'SET_IS_EXPANDED',
  SET_UNREAD_COUNT: 'SET_UNREAD_COUNT'
};

// Chat-specific reducer
const chatReducer = (state, action) => {
  switch (action.type) {
    case chatActions.SET_MESSAGES:
      return {
        ...state,
        messages: action.payload,
        loading: false,
        error: null
      };
    case chatActions.ADD_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload],
        unreadCount: state.isExpanded ? state.unreadCount : state.unreadCount + 1
      };
    case chatActions.SET_ONLINE_USERS:
      return {
        ...state,
        onlineUsers: action.payload
      };
    case chatActions.START_TYPING:
      const startUserId = action.payload;
      return {
        ...state,
        typingUsers: [...state.typingUsers, startUserId].filter((v, i, a) => a.indexOf(v) === i)
      };
    case chatActions.STOP_TYPING:
      const stopUserId = action.payload;
      return {
        ...state,
        typingUsers: state.typingUsers.filter(id => id !== stopUserId)
      };
    case chatActions.SET_IS_EXPANDED:
      return {
        ...state,
        isExpanded: action.payload,
        unreadCount: action.payload ? 0 : state.unreadCount // Clear unread when expanded
      };
    case chatActions.SET_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: action.payload
      };
    default:
      return state;
  }
};

const initialState = createBaseState({
  messages: [],
  onlineUsers: [],
  typingUsers: [],
  isExpanded: false,
  unreadCount: 0,
  currentUserId: null
});

export const ChatProvider = ({ children }) => {
  const { state, dispatch, setError, clearError } = useBaseContext(initialState, chatReducer);
  const { user, isAuthenticated } = useAuth();
  
  // Refs for cleanup
  const channelRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  // Initialize current user ID
  useEffect(() => {
    if (user?.id && state.currentUserId !== user.id) {
      dispatch({ type: chatActions.SET_UNREAD_COUNT, payload: 0 });
      dispatch({ type: 'SET_CUSTOM_FIELD', field: 'currentUserId', value: user.id });
    }
  }, [user?.id, state.currentUserId, dispatch]);

  // Send message
  const sendMessage = useCallback(async (message) => {
    if (!isAuthenticated || !user?.id || !message.trim()) return;

    try {
      const messageData = {
        message: message.trim(),
        user_id: user.id,
        message_type: 'text',
        is_dm: false
      };

      // Try with join first
      let { data, error } = await supabase
        .from('chat_messages')
        .insert([messageData])
        .select(`
          id,
          message,
          created_at,
          message_type,
          user_id,
          users!chat_messages_profile_user_fkey (
            username,
            avatar_url,
            country
          )
        `)
        .single();

      // If join fails, try without join
      if (error && error.code === 'PGRST200') {
        console.log('Sending message with fallback (no join)...');
        const { data: insertData, error: insertError } = await supabase
          .from('chat_messages')
          .insert([messageData])
          .select('*')
          .single();

        if (insertError) throw insertError;

        // Fetch user data separately
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, username, avatar_url, country_code')
          .eq('id', user.id)
          .single();

        if (userError) throw userError;

        data = {
          ...insertData,
          users: userData
        };
      } else if (error) {
        throw error;
      }
      
      // Message will be added via real-time subscription
      return data;
    } catch (error) {
      console.error('Failed to send message:', error);
      setError('Failed to send message');
      throw error;
    }
  }, [isAuthenticated, user?.id, setError]);

  // Typing indicator
  const sendTypingIndicator = useCallback(() => {
    if (!channelRef.current || !user?.id) return;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Send typing indicator
    channelRef.current.send({
      type: 'broadcast',
      event: 'typing',
      payload: { user_id: user.id, timestamp: Date.now() }
    });

    // Stop typing after 3 seconds
    typingTimeoutRef.current = setTimeout(() => {
      if (channelRef.current) {
        channelRef.current.send({
          type: 'broadcast',
          event: 'stop_typing',
          payload: { user_id: user.id }
        });
      }
    }, 3000);
  }, [user?.id]);

  // Toggle chat expansion
  const toggleExpanded = useCallback(() => {
    const newExpanded = !state.isExpanded;
    dispatch({ type: chatActions.SET_IS_EXPANDED, payload: newExpanded });
    
    // Save to localStorage
    localStorage.setItem('chat_expanded', newExpanded.toString());
  }, [state.isExpanded, dispatch]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    // Load initial messages
    const loadInitialMessages = async () => {
      try {
        // First try with join
        let { data, error } = await supabase
          .from('chat_messages')
          .select(`
            id,
            message,
            created_at,
            message_type,
            user_id,
            users!chat_messages_profile_user_fkey (
              username,
              avatar_url,
              country_code
            )
          `)
          .eq('is_dm', false)
          .is('match_id', null)
          .order('created_at', { ascending: true })
          .limit(50);

        // If join fails, try without join and fetch user data separately
        if (error && error.code === 'PGRST200') {
          const { data: messagesData, error: messagesError } = await supabase
            .from('chat_messages')
            .select('*')
            .eq('is_dm', false)
            .is('match_id', null)
            .order('created_at', { ascending: true })
            .limit(50);

          if (messagesError) throw messagesError;

          if (messagesData.length > 0) {
            // Fetch user data separately
            const userIds = [...new Set(messagesData.map(m => m.user_id))];
            
            const { data: usersData, error: usersError } = await supabase
              .from('users')
              .select('id, username, avatar_url, country_code')
              .in('id', userIds);

            if (usersError) throw usersError;

            // Combine the data
            data = messagesData.map(message => ({
              ...message,
              users: usersData.find(u => u.id === message.user_id)
            }));
          } else {
            data = [];
          }
        } else if (error) {
          throw error;
        }

        dispatch({ type: chatActions.SET_MESSAGES, payload: data || [] });
      } catch (error) {
        console.error('Failed to load chat messages:', error);
        setError('Failed to load chat messages');
      }
    };
    
    loadInitialMessages();

    // Set up real-time channel
    const channel = supabase.channel('global-chat', {
      config: {
        broadcast: { self: true },
        presence: { key: user.id }
      }
    })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages'
      }, async (payload) => {
        // Always use fallback approach for reliability
        try {
          const { data: messageData, error: messageError } = await supabase
            .from('chat_messages')
            .select('*')
            .eq('id', payload.new.id)
            .single();

          if (messageError) {
            console.error('Failed to fetch message data:', messageError);
            return;
          }

          // Skip DM messages and match messages in global chat
          if (messageData.is_dm || messageData.match_id) {
            return;
          }

          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id, username, avatar_url, country_code')
            .eq('id', messageData.user_id)
            .single();

          if (userError) {
            console.error('Failed to fetch user data:', userError);
            return;
          }

          const data = {
            ...messageData,
            users: userData
          };

          dispatch({ type: chatActions.ADD_MESSAGE, payload: data });
        } catch (error) {
          console.error('Failed to handle new message:', error);
        }
      })
      .on('presence', { event: 'sync' }, () => {
        const presenceState = channel.presenceState();
        const onlineUsers = Object.keys(presenceState);
        dispatch({ type: chatActions.SET_ONLINE_USERS, payload: onlineUsers });
      })
      .on('broadcast', { event: 'typing' }, (payload) => {
        // Only dispatch if it's not the current user
        if (payload.payload.user_id !== user.id) {
          dispatch({ 
            type: chatActions.START_TYPING, 
            payload: payload.payload.user_id
          });
        }
      })
      .on('broadcast', { event: 'stop_typing' }, (payload) => {
        // Only dispatch if it's not the current user
        if (payload.payload.user_id !== user.id) {
          dispatch({ 
            type: chatActions.STOP_TYPING, 
            payload: payload.payload.user_id
          });
        }
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Track presence
          await channel.track({ user_id: user.id, online_at: new Date().toISOString() });
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Chat channel error');
        } else if (status === 'TIMED_OUT') {
          console.error('Chat channel timed out');
        } else if (status === 'CLOSED') {
          console.log('Chat channel closed');
        }
      });

    channelRef.current = channel;

    // Restore expanded state from localStorage
    const savedExpanded = localStorage.getItem('chat_expanded');
    if (savedExpanded !== null) {
      dispatch({ type: chatActions.SET_IS_EXPANDED, payload: savedExpanded === 'true' });
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [isAuthenticated, user?.id, dispatch, setError]);

  const value = {
    // State
    messages: state.messages,
    onlineUsers: state.onlineUsers,
    typingUsers: state.typingUsers,
    isExpanded: state.isExpanded,
    unreadCount: state.unreadCount,
    loading: state.loading,
    error: state.error,
    
    // Actions
    sendMessage,
    sendTypingIndicator,
    toggleExpanded,
    clearError,
    
    // Utils
    isAuthenticated
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};